// Pages de contenu : villes (« transitaire + ville »), guides pratiques, FAQ. Texte dans villes.ts, guides.ts, faq.ts.
import { ArrowUpRight, Check, MapPin, MessageCircle } from 'lucide-react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { PageHero, Reveal } from '../components/Layout'
import { company, localePath, services, ui, type Locale } from '../content'
import { faq } from '../faq'
import { guides } from '../guides'
import { usePageMeta } from '../lib'
import { ariane, pageFaq, service } from '../seo'
import { photos, photoVille } from '../images'
import { villes, type Bloc, type Question } from '../villes'

const waLien = (texte: string) => company.whatsapp ? `https://wa.me/${company.whatsapp}?text=${encodeURIComponent(texte)}` : ''

// L'encadré qui suit la lecture : devis et WhatsApp, toujours à portée de main.
export function AsideCta({ locale, message }: { locale: Locale; message: string }) {
  const t = ui[locale]
  const wa = waLien(message)
  return <aside className="content-aside">
    <div className="aside-card">
      <strong>{locale === 'fr' ? 'Une opération à préparer ?' : 'هل لديكم عملية قيد الإعداد؟'}</strong>
      <p>{locale === 'fr' ? 'Décrivez-nous votre marchandise et votre trajet. Nous étudions votre demande avant de vous répondre.' : 'صفوا لنا بضاعتكم ومسارها. ندرس طلبكم قبل الرد عليكم.'}</p>
      <Link className="button button-primary" to={localePath(locale, '/demander-un-devis')}>{t.nav.quote}<ArrowUpRight /></Link>
      {wa && <a className="button button-secondary" href={wa} target="_blank" rel="noreferrer"><MessageCircle />{t.hero.whatsapp}</a>}
    </div>
  </aside>
}

export function Blocs({ blocs }: { blocs: Bloc[] }) {
  return <>{blocs.map(b => <section className="content-block" key={b.titre}>
    <h2>{b.titre}</h2>
    {b.texte.map(p => <p key={p}>{p}</p>)}
    {b.liste && <ul className="check-list">{b.liste.map(li => <li key={li}><Check />{li}</li>)}</ul>}
  </section>)}</>
}

export function FaqListe({ questions, titre }: { questions: Question[]; titre?: string }) {
  return <section className="content-block faq-block">
    {titre && <h2>{titre}</h2>}
    <div className="faq-list">{questions.map(({ q, r }) => <details key={q}><summary>{q}</summary><p>{r}</p></details>)}</div>
  </section>
}

function LiensVilles({ sauf }: { sauf?: string }) {
  return <div className="city-links">{villes.filter(v => v.slug !== sauf).map(v => <Link key={v.slug} to={localePath('fr', `/${v.slug}`)}><MapPin />{v.nom}</Link>)}</div>
}

export function VillesHub({ locale }: { locale: Locale }) {
  if (locale !== 'fr') return <Navigate to={localePath(locale)} replace />
  const titre = 'Transitaire au Maroc : dédouanement dans toutes les villes'
  const description = 'COGETRANS, transitaire basé à Casablanca depuis 2003, suit vos dossiers de dédouanement partout au Maroc : Tanger Med, Agadir, Nador, Marrakech, Rabat, Fès, Kénitra.'
  usePageMeta(titre, description, locale, [ariane(locale, [['Accueil', ''], ['Transitaire au Maroc', '/transitaire-maroc']]), service('Transit et dédouanement', description)])
  return <>
    <PageHero locale={locale} image={photos.tangerMed} title="Votre transitaire partout au Maroc" text="Basés à Casablanca depuis 2003, nous suivons vos dossiers de dédouanement dans les ports, les aéroports et les villes du pays." />
    <section className="section content-page">
      <div className="content-layout">
        <article className="content-main">
          <section className="content-block">
            <h2>Un seul interlocuteur, quel que soit le port d’arrivée</h2>
            <p>Votre marchandise peut arriver à Tanger Med alors que votre entrepôt est à Fès, ou partir d’Agadir vers l’Europe par la route. La déclaration en douane se fait en ligne : ce qui compte, c’est un dossier complet et un suivi attentif à chaque étape.</p>
            <p>Choisissez votre ville pour voir comment nous travaillons sur place : portes d’entrée, marchandises fréquentes et questions habituelles.</p>
          </section>
          <div className="city-grid">{villes.map((v, i) => <Reveal key={v.slug} delay={(i % 4) * .05}><Link className="city-card" style={{ '--city-photo': `url(${photos[photoVille[v.slug]]})` } as React.CSSProperties} to={localePath(locale, `/${v.slug}`)}>
            <span className="city-name">{v.nom}</span>
            <span className="city-ports">{v.portes.slice(0, 2).join(' · ')}</span>
            <ArrowUpRight />
          </Link></Reveal>)}</div>
          <section className="content-block">
            <h2>Nos services dans toutes les villes</h2>
            <div className="service-links">{services.map(s => { const Icon = s.icon; return <Link key={s.slug} to={localePath(locale, `/services/${s.slug}`)}><Icon /><span><strong>{s.title.fr}</strong><small>{s.short.fr}</small></span><ArrowUpRight /></Link> })}</div>
          </section>
        </article>
        <AsideCta locale={locale} message="Bonjour COGETRANS, j’ai une opération de dédouanement à préparer." />
      </div>
    </section>
  </>
}

export function VillePage({ locale }: { locale: Locale }) {
  const { slug } = useParams()
  const ville = villes.find(v => v.slug === slug)
  if (!ville || locale !== 'fr') return <Navigate to={localePath(locale)} replace />
  usePageMeta(ville.titre, ville.description, locale, [
    ariane(locale, [['Accueil', ''], ['Transitaire au Maroc', '/transitaire-maroc'], [ville.nom, `/${ville.slug}`]]),
    service(`Transitaire à ${ville.nom}`, ville.description, ville.nom),
    pageFaq(ville.faq),
  ])
  return <>
    <PageHero locale={locale} image={photos[photoVille[ville.slug]]} title={ville.h1} text={ville.chapo} />
    <section className="section content-page">
      <div className="content-layout">
        <article className="content-main">
          <section className="content-block ports-block">
            <h2>Les portes d’entrée à {ville.nom}</h2>
            <ul className="ports-list">{ville.portes.map(p => <li key={p}><MapPin />{p}</li>)}</ul>
          </section>
          <Blocs blocs={ville.blocs} />
          <section className="content-block">
            <h2>Nos services à {ville.nom}</h2>
            <div className="service-links">{services.map(s => { const Icon = s.icon; return <Link key={s.slug} to={localePath(locale, `/services/${s.slug}`)}><Icon /><span><strong>{s.title.fr}</strong><small>{s.short.fr}</small></span><ArrowUpRight /></Link> })}</div>
          </section>
          <FaqListe titre={`Questions fréquentes à ${ville.nom}`} questions={ville.faq} />
          <section className="content-block">
            <h2>Nous suivons aussi vos dossiers à</h2>
            <LiensVilles sauf={ville.slug} />
          </section>
        </article>
        <AsideCta locale={locale} message={`Bonjour COGETRANS, j’ai une opération à ${ville.nom} à préparer.`} />
      </div>
    </section>
  </>
}

export function GuidesHub({ locale }: { locale: Locale }) {
  if (locale !== 'fr') return <Navigate to={localePath(locale)} replace />
  const description = 'Guides pratiques pour importer et exporter au Maroc : étapes du dédouanement, documents, DUM, engagement d’importation, Incoterms, import de Chine.'
  usePageMeta('Guides import-export et dédouanement au Maroc', description, locale, [ariane(locale, [['Accueil', ''], ['Guides', '/guides']])])
  return <>
    <PageHero locale={locale} image={photos.documents} title="Guides pratiques de l’import-export" text="Les étapes, les documents et les mots de la douane, expliqués simplement. Pour préparer votre opération avant qu’elle parte." />
    <section className="section content-page">
      <div className="guide-grid">{guides.map((g, i) => <Reveal key={g.slug} delay={(i % 3) * .05}><Link className="guide-card" to={localePath(locale, `/guides/${g.slug}`)}>
        <h2>{g.h1}</h2><p>{g.description}</p><span className="text-link">Lire le guide<ArrowUpRight /></span>
      </Link></Reveal>)}</div>
    </section>
  </>
}

export function GuidePage({ locale }: { locale: Locale }) {
  const { slug } = useParams()
  const guide = guides.find(g => g.slug === slug)
  if (!guide || locale !== 'fr') return <Navigate to={localePath(locale, locale === 'fr' ? '/guides' : '')} replace />
  usePageMeta(guide.titre, guide.description, locale, [
    ariane(locale, [['Accueil', ''], ['Guides', '/guides'], [guide.h1, `/guides/${guide.slug}`]]),
    { '@context': 'https://schema.org', '@type': 'Article', headline: guide.h1, description: guide.description, inLanguage: 'fr', author: { '@id': 'https://xn--cogtrans-d1a.ma/#entreprise' }, publisher: { '@id': 'https://xn--cogtrans-d1a.ma/#entreprise' } },
    pageFaq(guide.faq),
  ])
  return <>
    <PageHero locale={locale} image={guides.indexOf(guide) % 2 ? photos.inspection : photos.documents} title={guide.h1} text={guide.chapo} />
    <section className="section content-page">
      <div className="content-layout">
        <article className="content-main">
          <Blocs blocs={guide.blocs} />
          <FaqListe titre="Questions fréquentes" questions={guide.faq} />
          <section className="content-block">
            <h2>À lire aussi</h2>
            <div className="related-guides">{guides.filter(g => g.slug !== guide.slug).slice(0, 4).map(g => <Link key={g.slug} to={localePath(locale, `/guides/${g.slug}`)}>{g.h1}<ArrowUpRight /></Link>)}</div>
          </section>
        </article>
        <AsideCta locale={locale} message="Bonjour COGETRANS, j’ai une question sur une opération d’import-export." />
      </div>
    </section>
  </>
}

export function FaqPage({ locale }: { locale: Locale }) {
  const fr = locale === 'fr'
  const titre = fr ? 'Questions fréquentes sur le transit et le dédouanement' : 'أسئلة شائعة حول العبور والتخليص الجمركي'
  const description = fr ? 'Transitaire, documents, délais, devis, villes : les réponses aux questions les plus fréquentes sur le dédouanement et l’import-export au Maroc.' : 'المعشر، الوثائق، الآجال، عرض السعر، المدن: أجوبة عن الأسئلة الأكثر شيوعاً حول التخليص الجمركي والاستيراد والتصدير بالمغرب.'
  usePageMeta(fr ? 'FAQ transit et dédouanement au Maroc' : 'أسئلة شائعة حول التخليص الجمركي بالمغرب', description, locale, [ariane(locale, [[fr ? 'Accueil' : 'الرئيسية', ''], ['FAQ', '/faq']]), pageFaq(faq[locale])])
  return <>
    <PageHero locale={locale} image={photos.documents} title={titre} text={fr ? 'Vous ne trouvez pas votre réponse ? Écrivez-nous sur WhatsApp ou demandez un devis.' : 'لم تجدوا جوابكم؟ راسلونا عبر واتساب أو اطلبوا عرض سعر.'} />
    <section className="section content-page">
      <div className="content-layout">
        <article className="content-main"><FaqListe questions={faq[locale]} />{fr && <section className="content-block"><h2>Pour aller plus loin</h2><div className="related-guides">{guides.slice(0, 4).map(g => <Link key={g.slug} to={localePath(locale, `/guides/${g.slug}`)}>{g.h1}<ArrowUpRight /></Link>)}</div></section>}</article>
        <AsideCta locale={locale} message={fr ? 'Bonjour COGETRANS, j’ai une question.' : 'مرحباً كوجيترانس، لدي سؤال.'} />
      </div>
    </section>
  </>
}
